import React, { useEffect, useState, useMemo } from 'react';
import { 
  Button, Box, Container, Paper, Typography, Chip, CircularProgress, Backdrop,
  TextField, MenuItem, Grid, InputAdornment, IconButton, Tooltip 
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { usePaginationWithUrl } from '../../../hooks/usePaginationWithUrl';
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import * as invoiceService from '../services/invoiceService';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import useStoreInfo from '../../../hooks/useCompanyInfo';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import { PAYMENT_STATUS, getPaymentStatus } from '../../../constants/paymentStatus';

const ListInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Filter states
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Get payment methods from Redux
  const { paymentMethods } = useSelector((state) => state.paymentMethod);
  
  // Get available quarters based on current month
  const getAvailableQuarters = () => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const quarters = [];
    
    if (currentMonth >= 3) quarters.push({ value: 'q1', label: 'Q1 (Jan-Mar)' });
    if (currentMonth >= 6) quarters.push({ value: 'q2', label: 'Q2 (Apr-Jun)' });
    if (currentMonth >= 9) quarters.push({ value: 'q3', label: 'Q3 (Jul-Sep)' });
    if (currentMonth >= 12) quarters.push({ value: 'q4', label: 'Q4 (Oct-Dec)' });
    
    return quarters;
  };
  
  const availableQuarters = getAvailableQuarters();
  
  // ✅ Get store info from Redux store (auto-fetches if needed)
  const { storeInfo, loading: storeLoading } = useStoreInfo();
  
  // ✅ Pagination state with URL sync
  const { page, pageSize, handlePageChange, handlePageSizeChange } = usePaginationWithUrl({
    defaultPage: 0,
    defaultPageSize: 10,
    pageSizeOptions: [5, 10, 25, 50, 100]
  });
  
  // Convert to DataGrid format
  const paginationModel = { page, pageSize };
  const setPaginationModel = (model) => {
    if (model.page !== page) {
      handlePageChange(null, model.page);
    }
    if (model.pageSize !== pageSize) {
      handlePageSizeChange({ target: { value: model.pageSize.toString() } });
    }
  };
  
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch payment methods on mount
  useEffect(() => {
    if (paymentMethods.length === 0) {
      dispatch(fetchPaymentMethods());
    }
  }, [dispatch, paymentMethods.length]);

  useEffect(() => {
    fetchInvoices();
  }, [paginationModel.page, paginationModel.pageSize]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Build query parameters for backend filtering
      const params = {
        PageNumber: paginationModel.page + 1, // Backend uses 1-based pagination
        PageSize: paginationModel.pageSize,
        SearchTerm: searchQuery || undefined,
        InvoiceStatus: paymentStatusFilter !== 'all' ? paymentStatusFilter : undefined,
        PaymentMethodId: paymentMethodFilter !== 'all' ? paymentMethods.find(m => m.name === paymentMethodFilter)?.id : undefined,
        StartDate: dateFilter !== 'all' && dateFilter === 'custom' && customStartDate ? customStartDate : undefined,
        EndDate: dateFilter !== 'all' && dateFilter === 'custom' && customEndDate ? customEndDate : undefined,
        SortBy: 'InvoiceDate',
        SortDirection: 'desc'
      };
      
      // Apply date range filters for predefined ranges
      if (dateFilter && dateFilter !== 'all' && dateFilter !== 'custom') {
        const dateRange = getDateRange();
        if (dateRange) {
          params.StartDate = dateRange.start.toISOString().split('T')[0];
          params.EndDate = dateRange.end.toISOString().split('T')[0];
        }
      }
      
      const data = await invoiceService.getInvoices(params);
      
      // Backend returns { Items: [], TotalItems: number }
      if (data && data.Items) {
        setInvoices(data.Items);
        setTotalCount(data.TotalItems || 0);
        
      } else {
        setInvoices([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.error('Failed to load invoices');
      setInvoices([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Refresh invoices with new API call
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchInvoices();
    } finally {
      setRefreshing(false);
    }
  };
  
  // Get date range based on filter selection
  const getDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case 'today': {
        return { start: today, end: new Date(today.getTime() + 86400000 - 1) };
      }
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: new Date(yesterday.getTime() + 86400000 - 1) };
      }
      case 'weekly': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { start: weekAgo, end: today };
      }
      case 'monthly': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: monthStart, end: today };
      }
      case 'q1': {
        const year = today.getFullYear();
        return { start: new Date(year, 0, 1), end: new Date(year, 2, 31, 23, 59, 59) };
      }
      case 'q2': {
        const year = today.getFullYear();
        return { start: new Date(year, 3, 1), end: new Date(year, 5, 30, 23, 59, 59) };
      }
      case 'q3': {
        const year = today.getFullYear();
        return { start: new Date(year, 6, 1), end: new Date(year, 8, 30, 23, 59, 59) };
      }
      case 'q4': {
        const year = today.getFullYear();
        return { start: new Date(year, 9, 1), end: new Date(year, 11, 31, 23, 59, 59) };
      }
      case 'yearly': {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return { start: yearStart, end: today };
      }
      case 'custom': {
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        return null;
      }
      default:
        return null;
    }
  };

  // Apply filters button handler - triggers new fetch
  const handleApplyFilters = () => {
    // Reset to first page when filters change
    setPaginationModel({ ...paginationModel, page: 0 });
    fetchInvoices();
  };

  // Reset all filters
  const handleResetFilters = () => {
    setPaymentStatusFilter('all');
    setPaymentMethodFilter('all');
    setSearchQuery('');
    setDateFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setPaginationModel({ page: 0, pageSize: 10 });
    fetchInvoices();
  };

  // Format date helper
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const columns = [
    {
      field: 'CustomerName',
      headerName: 'Customer',
      flex: 1.2,
      minWidth: 130,
      renderCell: (params) => {
        const customerName = params.row.CustomerName || '-';
        const customerVAT = params.row.CustomerVATNumber || '';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
              {customerName}
            </Typography>
            {customerVAT && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {customerVAT}
              </Typography>
            )}
          </Box>
        );
      },
    },
    { field: 'InvoiceNumber', headerName: 'Invoice #', flex: 0.8, minWidth: 100 },
    {
      field: 'InvoiceDate',
      headerName: 'Invoice Date',
      flex: 0.9,
      minWidth: 105,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const invoiceDate = params.row.InvoiceDate;
        if (!invoiceDate) return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" sx={{ fontSize: '0.813rem' }}>N/A</Typography>
          </Box>
        );
        const d = new Date(invoiceDate);
        const datePart = formatDate(invoiceDate);
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const timePart = `${hours}:${minutes}`;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" sx={{ fontSize: '0.813rem' }}>{datePart}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{timePart}</Typography>
          </Box>
        );
      }
    },
    {
      field: 'ProductCount',
      headerName: 'Items',
      flex: 0.4,
      minWidth: 60,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.row.LineItemsCount || 0,
    },
    {
      field: 'InvoiceTotalAmount',
      headerName: 'Total',
      flex: 0.7,
      align: 'center',
      minWidth: 85,
      headerAlign: 'center',
      renderCell: (params) => {
        const totalAmount = Number(params.row.InvoiceTotalAmount || 0);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.813rem' }}>
              €{totalAmount.toFixed(2)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'InvoicePaidAmount',
      headerName: 'Paid',
      flex: 0.7,
      align: 'center',
      minWidth: 85,
      headerAlign: 'center',
      renderCell: (params) => {
        const paidAmount = Number(params.row.InvoicePaidAmount || 0);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" fontWeight={500} sx={{ color: '#4caf50', fontSize: '0.813rem' }}>
              €{paidAmount.toFixed(2)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'Status',
      headerName: 'Payment Status',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const paid = Number(params.row.InvoicePaidAmount) || 0;
        const total = Number(params.row.InvoiceTotalAmount) || 0;
        const label = getPaymentStatus(paid, total);
        let bg = '';
        if (label === PAYMENT_STATUS.NOT_PAID) {
          bg = '#d32f2f';
        } else if (label === PAYMENT_STATUS.PAID) {
          bg = '#388e3c';
        } else {
          bg = '#fbc02d';
        }
        return (
          <span
            style={{
              color: '#fff',
              background: bg,
              borderRadius: 10,
              padding: '2px 6px',
              fontWeight: 500,
              fontSize: 10,
              display: 'inline-block',
              minWidth: 65,
              lineHeight: 1.5,
              textAlign: 'center',
              letterSpacing: 0.2,
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      field: 'PaymentMethod',
      headerName: 'Payment',
      flex: 1,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const payments = params.row.Payments;
        const paymentMethod = params.row.PaymentMethod;
        const paidAmount = Number(params.row.InvoicePaidAmount || 0);

        if (payments && payments.length > 0) {
          const aggregated = getAggregatedPayments(payments);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center', justifyContent: 'center', height: '100%', flexWrap: 'wrap' }}>
              {aggregated.map((payment, index) => (
                <Chip
                  key={index}
                  label={payment.methodName}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', fontWeight: 500 }}
                />
              ))}
            </Box>
          );
        } else if (paidAmount > 0 && paymentMethod && !paymentMethod.includes('/')) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Chip
                label={paymentMethod}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.7rem', fontWeight: 500 }}
              />
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>N/A</Typography>
          </Box>
        );
      }
    },
    {
      field: 'PeppolStatus',
      headerName: 'Peppol Status',
      flex: 0.8,
      minWidth: 95,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSendOnPeppol = params.row.IsSendOnPeppol;
        const peppolStatus = params.row.PeppolStatus || 'Not Sent';

        let icon;
        let color;
        let label;

        if (!isSendOnPeppol) {
          icon = <CancelIcon />;
          color = 'default';
          label = 'N/A';
        } else {
          switch (peppolStatus.toLowerCase()) {
            case 'sent':
            case 'delivered':
            case 'success':
              icon = <CheckCircleIcon />;
              color = 'success';
              label = 'Sent';
              break;
            case 'pending':
            case 'processing':
              icon = <PendingIcon />;
              color = 'warning';
              label = 'Pending';
              break;
            case 'failed':
            case 'error':
              icon = <CancelIcon />;
              color = 'error';
              label = 'Failed';
              break;
            default:
              icon = <PendingIcon />;
              color = 'info';
              label = 'Not Sent';
          }
        }

        return (
          <Chip
            icon={icon}
            label={label}
            color={color}
            size="small"
            sx={{ fontWeight: 600, fontSize: 10, minWidth: 70 }}
          />
        );
      },
    },
    {
      field: 'InvoiceStatus',
      headerName: 'Invoice Status',
      flex: 0.7,
      minWidth: 95,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = params.row.InvoiceStatus || 'Draft';
        const isSent = status === 'Sent';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Chip
              label={status}
              size="small"
              color={isSent ? 'success' : 'warning'}
              sx={{ fontWeight: 600, fontSize: 10, minWidth: 60 }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate(`/invoices/view/${params.row.Id}`, { state: { returnUrl: location.pathname + location.search } })}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 10,
            padding: '2px 8px',
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Invoices
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your invoices
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Refresh invoices">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.3) 100%)',
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={24} sx={{ color: '#667eea' }} />
                ) : (
                  <RefreshIcon sx={{ color: '#667eea' }} />
                )}
              </IconButton>
            </Tooltip>
            
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => navigate('/invoices/add')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.2s',
                },
              }}
            >
              Add Invoice
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Filter Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ fontSize: 24, color: '#667eea', mr: 1 }} />
          <Typography variant="h6" fontWeight="600" sx={{ color: '#667eea' }}>
            Filters
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Search Field */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by customer, VAT, or invoice #"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#667eea' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            />
          </Grid>

          {/* Payment Status Filter */}
          <Grid item xs={12} md={2.25}>
            <TextField
              fullWidth
              select
              size="small"
              label="Payment Status"
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value={PAYMENT_STATUS.PAID}>{PAYMENT_STATUS.PAID}</MenuItem>
              <MenuItem value={PAYMENT_STATUS.PARTIAL_PAID}>{PAYMENT_STATUS.PARTIAL_PAID}</MenuItem>
              <MenuItem value={PAYMENT_STATUS.NOT_PAID}>{PAYMENT_STATUS.NOT_PAID}</MenuItem>
            </TextField>
          </Grid>

          {/* Payment Method Filter */}
          <Grid item xs={12} md={2.25}>
            <TextField
              fullWidth
              select
              size="small"
              label="Payment Method"
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            >
              <MenuItem value="all">All Methods</MenuItem>
              {paymentMethods.map((method) => (
                <MenuItem key={method.id} value={method.name}>
                  {method.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Date Filter */}
          <Grid item xs={12} md={2.25}>
            <TextField
              fullWidth
              select
              size="small"
              label="Date Range"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ color: '#667eea', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            >
              <MenuItem value="all">All Dates</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="weekly">Last 7 Days</MenuItem>
              <MenuItem value="monthly">This Month</MenuItem>
              {availableQuarters.map((quarter) => (
                <MenuItem key={quarter.value} value={quarter.value}>
                  {quarter.label}
                </MenuItem>
              ))}
              <MenuItem value="yearly">This Year</MenuItem>
              <MenuItem value="custom">Custom Date Range</MenuItem>
            </TextField>
          </Grid>
          
          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              </Grid>
            </>
          )}

          {/* Reset and Apply Filters Buttons */}
          {dateFilter !== 'custom' ? (
            <>
              <Grid item xs={12} md={1.25}>
                <Button
                  variant="outlined"
                  onClick={handleResetFilters}
                  sx={{
                    height: '40px',
                    borderRadius: 2,
                    borderColor: '#667eea',
                    color: '#667eea',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#667eea',
                      backgroundColor: 'rgba(102, 126, 234, 0.04)',
                    },
                  }}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleApplyFilters}
                  sx={{
                    height: '40px',
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                    },
                  }}
                >
                  Apply
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleResetFilters}
                  sx={{
                    height: '40px',
                    borderRadius: 2,
                    borderColor: '#667eea',
                    color: '#667eea',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#667eea',
                      backgroundColor: 'rgba(102, 126, 234, 0.04)',
                    },
                  }}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleApplyFilters}
                  sx={{
                    height: '40px',
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                    },
                  }}
                >
                  Apply
                </Button>
              </Grid>
            </>
          )}
        </Grid>

        {/* Active Filters Summary */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {(paymentStatusFilter !== 'all' || paymentMethodFilter !== 'all' || searchQuery || dateFilter !== 'all') && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Active Filters:
              </Typography>
              {paymentStatusFilter !== 'all' && (
                <Chip
                  label={`Status: ${paymentStatusFilter}`}
                  size="small"
                  onDelete={() => setPaymentStatusFilter('all')}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
              {paymentMethodFilter !== 'all' && (
                <Chip
                  label={`Method: ${paymentMethods.find(m => m.name === paymentMethodFilter)?.label || paymentMethodFilter}`}
                  size="small"
                  onDelete={() => setPaymentMethodFilter('all')}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={() => setSearchQuery('')}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
              {dateFilter !== 'all' && (
                <Chip
                  label={`Date: ${dateFilter === 'custom' ? `${customStartDate} to ${customEndDate}` : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1).replace(/[qQ]\d/, (m) => m.toUpperCase())}`}
                  size="small"
                  onDelete={() => { setDateFilter('all'); setCustomStartDate(''); setCustomEndDate(''); }}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
            </>
          )}
        </Box>

        {/* Results Count */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{invoices.length}</strong> of <strong>{totalCount}</strong> invoices (Page {paginationModel.page + 1})
          </Typography>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <ReusableDataGrid
          rows={invoices}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.04)',
            },
          }}
        />
      </Paper>
    </Container>
  );
};

export default ListInvoices;